import { Box, Button, Stack, Typography } from '@mui/material';
import { useStatsigClient } from '@statsig/react-bindings';
import Uppy, { UppyFile } from '@uppy/core';
import { Form, Formik, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import { object, string } from 'yup';

import MessageCenterHeader from '../../components/MessageCenterHeader';
import useITRConfig from '../../hooks/useITRConfig';

import { UploadedFile } from './NewMessage.interface';
import FilesUploader from './components/FilesUploader';

import mapZendeskErrorToMessage, { handleUploadErrors } from 'modules/IMC/MessageCenter/utils/zendeskErrorHandler';
import GoBack from 'modules/IMC/common/IMCHeader/actions/GoBack';
import GenericSearchBar from 'modules/IMC/common/IMCHeader/components/GenericSearchBar';
import autocompleteOptions from 'modules/IMC/common/IMCHeader/components/GenericSearchBar/AutocompleteSearchBar/autocompleteOptions';
import { useIMCContext } from 'modules/IMC/common/context';
import FormControlWithLabel from 'modules/Onboarding/common/FormControlWithLabel';
import { collectiveTracking } from 'modules/common/components/AnalyticsTracking/utils';
import HelpAgentIcon from 'modules/common/components/Icons/HelpAgentIcon';
import TextField from 'modules/common/components/MUI/TextField';
import { useNavigationUpdate } from 'modules/common/components/Navigator/context';
import { MessageCenterScreen } from 'modules/common/components/Navigator/context/NavigatorContext.interface';
import DrawerScreenLayout from 'modules/common/components/RightDrawer/DrawerScreenLayout';
import { DynamicConfigs } from 'modules/common/constants/featuregates';
import useNewMessage from 'services/help-center/mutations/useNewMessage';

interface NewMessageValues {
    subject: string;
    description: string;
    files: UploadedFile[];
}

const validationSchema = object({
    subject: string().typeError('Subject is required').required('Subject is required'),
    description: string().typeError('Description is required').required('Description is required'),
});

interface NewMessageProps {
    subject: string;
    description: string;
    descriptionPlaceholder?: string;
    tags?: string[];
    customMessage?: string;
}

function NewMessage({
    subject,
    description,
    descriptionPlaceholder = '',
    tags = [],
    customMessage = '',
}: NewMessageProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const uppyRef = useRef<Uppy | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<UppyFile<any, any>[]>([]);
    const [lastSentContent, setLastSentContent] = useState<string | null>(null);

    const createNewMessage = useNewMessage();
    const { navigateToPage } = useNavigationUpdate();
    const { enqueueSnackbar } = useSnackbar();
    const { client } = useStatsigClient();
    const { itrTag } = useITRConfig();
    const { openHelpAgentWidget, isHelpAgentWidgetOpen } = useIMCContext();

    const DEFAULT_MESSAGE = "Your message is with our team. We'll get back to you within a business day.";
    const responseTimeConfig = client.getDynamicConfig(DynamicConfigs.IMCResponseTimeMessage);
    const statsigMessage = responseTimeConfig.get('message', DEFAULT_MESSAGE);

    // Priority: explicit customMessage → Statsig → default
    const resolvedMessage = customMessage?.trim() || statsigMessage?.trim() || DEFAULT_MESSAGE;

    // Merge provided tags with page-context tags (e.g., 'itr' when on ITR pages)
    const finalTags = [...new Set([...tags, ...itrTag])];

    const onSubmit = async (values: NewMessageValues, formikHelpers: FormikHelpers<NewMessageValues>) => {
        let filesTokens: string[] = [];

        const clearAttachments = () => {
            const uppy = uppyRef.current;
            if (!uppy) {
                return;
            }
            uppy.getFiles().forEach((file) => uppy.removeFile(file.id));
        };

        if (uppyRef.current) {
            const uppy = uppyRef.current;
            let result;
            try {
                result = await uppy.upload();
            } catch (error) {
                enqueueSnackbar(mapZendeskErrorToMessage(error), { variant: 'error' });
                formikHelpers.setSubmitting(false);
                return;
            }

            // Alert specific errors but allow retry with form information preserved
            if (handleUploadErrors(result, { enqueueSnackbar })) {
                formikHelpers.setSubmitting(false);
                return;
            }

            // TypeScript guard: result is valid after handleUploadErrors check
            if (result && 'successful' in result && Array.isArray(result.successful)) {
                filesTokens = result.successful.map((file: any) => file.response.body.upload.token);
            }
        }

        try {
            const newTicket = await createNewMessage(
                values.subject,
                values.description,
                filesTokens,
                finalTags,
                resolvedMessage
            );

            collectiveTracking.track({
                name: 'New Conversation Created',
                properties: {
                    category: 'message_center',
                    action: 'click',
                    conversation_id: newTicket.id,
                    timestamp: new Date().toISOString(),
                    user_id: newTicket.requester_id,
                    initial_topic: values.subject,
                    files: values.files?.length ?? 0,
                    tags: finalTags,
                    is_help_agent_open: isHelpAgentWidgetOpen ? 'true' : 'false',
                    help_agent_experiment: 'widget',
                },
            });
            clearAttachments();
            formikHelpers.resetForm();
            navigateToPage(MessageCenterScreen.CHAT, { id: newTicket.id });
        } catch (error) {
            // Alert specific errors but allow retry with form information preserved
            enqueueSnackbar(mapZendeskErrorToMessage(error), { variant: 'error' });
            formikHelpers.setSubmitting(false);
        }
    };

    const initialValues: NewMessageValues = {
        subject: subject ?? '',
        description: description ?? '',
        files: [],
    };

    return (
        <DrawerScreenLayout containerRef={containerRef}>
            <MessageCenterHeader containerRef={containerRef} startAction={GoBack} compressed />
            <Stack sx={{ padding: 3, height: '100%' }}>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                    // Validate on blur/submit, not per keystroke — the free-text
                    // description field otherwise re-runs the whole schema on every
                    // character (wasteful, and flakes user.type tests — AP-1875).
                    validateOnChange={false}>
                    {({ setFieldValue, setFieldTouched, values, errors, touched, isSubmitting }) => {
                        return (
                            <Box sx={{ height: '100%', '& > form': { height: '100%' } }}>
                                <Form>
                                    <Stack sx={{ height: '100%' }}>
                                        <FormControlWithLabel htmlFor="subject" label="Subject">
                                            <GenericSearchBar
                                                placeholder="Subject"
                                                autoCompleteOptions={autocompleteOptions}
                                                value={values.subject}
                                                onChange={(event, value) => setFieldValue('subject', value)}
                                                onBlur={() => setFieldTouched('subject')}
                                                textFieldProps={{
                                                    error: touched.subject && !!errors.subject,
                                                    helperText: touched.subject && errors.subject,
                                                    InputProps: {
                                                        startAdornment: '',
                                                    },
                                                }}
                                            />
                                        </FormControlWithLabel>
                                        <FormControlWithLabel
                                            htmlFor="description"
                                            label="Description"
                                            sx={{ flexGrow: 1 }}>
                                            <Box sx={{ position: 'relative', flexGrow: 1, minHeight: '200px' }}>
                                                <TextField
                                                    name="description"
                                                    id="description"
                                                    multiline
                                                    placeholder={
                                                        descriptionPlaceholder ||
                                                        'Share details to help us provide you with the best support.'
                                                    }
                                                    sx={{
                                                        height: '100%',
                                                        width: '100%',
                                                        '& .MuiInputBase-root': {
                                                            height: '100%',
                                                            alignItems: 'flex-start',
                                                            pb:
                                                                values.description.trim().length > 0 &&
                                                                uploadedFiles.length === 0
                                                                    ? 11
                                                                    : 2,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </FormControlWithLabel>
                                        <FormControlWithLabel htmlFor="files" label="Attachments" sx={{ flexGrow: 1 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    marginBottom: 1,
                                                    height: '100%',
                                                    '& > div, & > div > div, & .uppy-Root, & .uppy-Dashboard, & .uppy-Dashboard-inner, & .uppy-Dashboard-innerWrap, & .uppy-Dashboard-AddFiles':
                                                        {
                                                            height: '100%!important',
                                                        },
                                                }}>
                                                <FilesUploader
                                                    uppyRef={uppyRef}
                                                    onFilesChange={setUploadedFiles}
                                                    disabled={isSubmitting}
                                                />
                                            </Typography>
                                        </FormControlWithLabel>
                                        <Stack direction="column" spacing={0.5}>
                                            <Button
                                                onClick={() => {
                                                    setLastSentContent(values.description);
                                                    openHelpAgentWidget({
                                                        message: values.description,
                                                        source: 'get_instant_message_new_message_screen',
                                                    });
                                                }}
                                                variant="contained"
                                                size="large"
                                                aria-label="Get instant response"
                                                disabled={
                                                    uploadedFiles.length > 0 ||
                                                    (lastSentContent !== null && lastSentContent === values.description)
                                                }>
                                                <HelpAgentIcon color="white" fontSize="small" sx={{ mr: 1 }} />
                                                Get instant response
                                            </Button>
                                            <Button
                                                loading={isSubmitting}
                                                type="submit"
                                                variant="text"
                                                size="large"
                                                color="secondary"
                                                aria-label="Send message">
                                                Send message
                                            </Button>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'text.secondary' }}
                                                textAlign="center">
                                                Our team typically responds within one business day
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Form>
                            </Box>
                        );
                    }}
                </Formik>
            </Stack>
        </DrawerScreenLayout>
    );
}

export default NewMessage;
